/**
 * Single source of truth for the session JWT.
 *
 * localStorage on its own is a write-only sink as far as React is concerned:
 * a component that read the token at mount never learns that another component
 * replaced it. Every token write therefore goes through setToken()/clearToken(),
 * which notify in-tab subscribers; the browser's own `storage` event covers
 * other tabs of the same origin.
 *
 * Components read reactively with useAuthToken(); non-React callers use
 * getToken().
 */
import { useSyncExternalStore } from 'react'

const STORAGE_KEY = 'token'

type Listener = () => void

const listeners = new Set<Listener>()

function notify() {
  for (const listener of listeners) listener()
}

export function getToken(): string | null {
  return localStorage.getItem(STORAGE_KEY)
}

export function setToken(token: string): void {
  localStorage.setItem(STORAGE_KEY, token)
  notify()
}

export function clearToken(): void {
  localStorage.removeItem(STORAGE_KEY)
  notify()
}

/** Subscribe to token changes in this tab and in other tabs. Returns unsubscribe. */
export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  if (listeners.size === 1) {
    window.addEventListener('storage', onStorage)
  }
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      window.removeEventListener('storage', onStorage)
    }
  }
}

function onStorage(e: StorageEvent) {
  // key === null on localStorage.clear()
  if (e.key === null || e.key === STORAGE_KEY) notify()
}

/** Reactive token value — re-renders the caller whenever the JWT changes. */
export function useAuthToken(): string | null {
  return useSyncExternalStore(subscribe, getToken, () => null)
}

/**
 * Decoded JWT payload, or null when there is no token / it is malformed.
 */
export function decodeToken(token: string | null): Record<string, unknown> | null {
  if (!token) return null
  try {
    return JSON.parse(atob(token.split('.')[1]))
  } catch {
    return null
  }
}
